import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Button,
  Divider,
  alpha,
  useTheme,
  Paper,
  Container,
} from "@mui/material";
import {
  TrackChanges,
  Dashboard,
  Videocam,
  Movie,
  LibraryBooks,
  Web,
  Chat,
  ChevronLeft,
  ChevronRight,
  Send,
} from "@mui/icons-material";
import Header from "../Header";
import IntendedLearners from "./intended-learners";
import CourseStructure from "./course-structure";
import SetupVideo from "./setup-video";
import FilmEdit from "./film-edit";
import Curriculum from "./curriculum";
import CourseLandingPage from "./course-landing-page";
import CourseMessages from "./course-messages";

const drawerWidth = 280;
const GOLD = "#D4AF37";

export default function AddCourseInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get("view") || "intended-learners";
  const [currentView, setCurrentView] = useState(initialView);

  useEffect(() => {
    setCurrentView(initialView);
  }, [initialView]);

  const menuSections = [
    {
      title: "Plan your course",
      items: [
        {
          id: "intended-learners",
          label: "Intended learners",
          icon: <TrackChanges />,
        },
        {
          id: "course-structure",
          label: "Course structure",
          icon: <Dashboard />,
        },
        { id: "setup-video", label: "Setup & test video", icon: <Videocam /> },
      ],
    },
    {
      title: "Create your content",
      items: [
        { id: "film-edit", label: "Film & edit", icon: <Movie /> },
        { id: "curriculum", label: "Curriculum", icon: <LibraryBooks /> },
      ],
    },
    {
      title: "Publish your course",
      items: [
        {
          id: "courselandingpage",
          label: "Course landing page",
          icon: <Web />,
        },
        { id: "course-messages", label: "Course messages", icon: <Chat /> },
      ],
    },
  ];

  const renderView = () => {
    switch (currentView) {
      case "intended-learners":
        return <IntendedLearners courseId={id} />;
      case "course-structure":
        return <CourseStructure courseId={id} />;
      case "setup-video":
        return <SetupVideo courseId={id} />;
      case "film-edit":
        return <FilmEdit courseId={id} />;
      case "curriculum":
        return <Curriculum courseId={id} />;
      case "courselandingpage":
        return <CourseLandingPage courseId={id} />;
      case "course-messages":
        return <CourseMessages courseId={id} />;
      default:
        return <IntendedLearners courseId={id} />;
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8f9fa" }}>
      <Header />

      {/* Instructor Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            top: "64px",
            height: "calc(100vh - 64px)",
            bgcolor: "#1A1A1A",
            color: "white",
            border: "none",
            boxShadow: "Shadows.3",
          },
        }}
      >
        <Box sx={{ p: 3, pt: 4 }}>
          {menuSections.map((section, idx) => (
            <Box key={section.title} sx={{ mb: 4 }}>
              <Typography
                variant="overline"
                sx={{
                  color: alpha("#fff", 0.5),
                  fontWeight: 800,
                  letterSpacing: "1px",
                  mb: 1,
                  display: "block",
                  px: 2,
                }}
              >
                {section.title}
              </Typography>
              <List disablePadding>
                {section.items.map((item) => {
                  const active = currentView === item.id;
                  return (
                    <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setCurrentView(item.id)}
                        sx={{
                          borderRadius: "12px",
                          mx: 1,
                          bgcolor: active ? alpha(GOLD, 0.1) : "transparent",
                          color: active ? GOLD : alpha("#fff", 0.7),
                          "&:hover": {
                            bgcolor: alpha(GOLD, 0.15),
                            color: GOLD,
                            "& .MuiListItemIcon-root": { color: GOLD },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: active ? GOLD : alpha("#fff", 0.4),
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: active ? 700 : 500,
                            fontSize: "0.9rem",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
              {idx < menuSections.length - 1 && (
                <Divider sx={{ mt: 2, bgcolor: alpha("#fff", 0.1) }} />
              )}
            </Box>
          ))}

          <Box sx={{ mt: 4, px: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Send />}
              onClick={() => navigate(`/db-shiksha/kaas/student/course/${id}`)}
              sx={{
                bgcolor: GOLD,
                borderRadius: "50px",
                fontWeight: 800,
                py: 1.5,
                "&:hover": { bgcolor: alpha(GOLD, 0.8) },
              }}
            >
              Submit Course
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          ml: 0,
          mt: "64px",
          bgcolor: "background.default",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 5 },
              borderRadius: 6,
              border: "1px solid",
              borderColor: "divider",
              minHeight: "600px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
            }}
          >
            {renderView()}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
